import { effect, inject, Injectable, Injector } from '@angular/core';
import { DaisiFeature } from '@daisi/models/daisi-feature-collection';
import { Subscription, timer } from 'rxjs';
import { Map as MaplibreMap, StyleImage } from 'maplibre-gl';
import { IncidentStatus } from '@generated/backend-api/model/incident-status';
import { IncidentsDataService } from '@services/data/incidents-data.service';
import {
  DATEX_TYPE_TO_INACTIVE_MAP_ICON_MAP,
  DATEX_TYPE_TO_MAP_ICON_MAP,
} from '@services/map/incident-icon-images';

@Injectable({ providedIn: 'root' })
export class IncidentIconService {
  private readonly imDataService = inject(IncidentsDataService);
  private readonly injector = inject(Injector);

  public static readonly NORMAL_SIZE = 1;
  public static readonly LARGE_SIZE = 1.5;
  public static readonly DATEX_TYPE_TO_MAP_ICON_MAP = DATEX_TYPE_TO_MAP_ICON_MAP;
  public static readonly DATEX_TYPE_TO_INACTIVE_MAP_ICON_MAP = DATEX_TYPE_TO_INACTIVE_MAP_ICON_MAP;

  private readonly neededIcons = new Set<string>();
  private readonly iconSvgByIdentifier = new Map<string, { normal: string; large: string }>();

  private pollingSubscription: Subscription | undefined;
  private initialized = false;

  private readonly addedIconsByMap = new Map<MaplibreMap, Set<string>>();

  /**
   * This service will keep the map up-to-date with all needed icons to display the current set of incidents.
   *
   * This is how it works:
   * - It waits for a map to be loaded. Loaded in this case means that the maps has loaded the icon sprite, as
   *   we need this sprite to create our svg's.
   * - Once the first map has loaded, all needed SVGs (both normal and large size) are created and stored.
   * - From this moment on, all maps that have been registered through this method will receive all svgs.
   * - Whenever a new svg is needed all maps will be updated.
   */
  public addIncidentIconsToMap(map: MaplibreMap) {
    if (this.addedIconsByMap.has(map)) {
      return; // already added
    }

    this.addedIconsByMap.set(map, new Set());

    if (this.initialized) {
      this.updateMaps();
    } else if (!this.pollingSubscription) {
      // The first map has been added, now we start polling for the map to have loaded the sprite.
      this.pollingSubscription = timer(100, 100).subscribe(() => {
        if (map.hasImage(IncidentIconService.DATEX_TYPE_TO_MAP_ICON_MAP.Accident)) {
          // The map has loaded! Now we use this map's sprite to create all SVGs, which we will reuse for other maps.
          this.startAddingIcons(map);
          this.pollingSubscription?.unsubscribe();
          this.pollingSubscription = undefined;
          this.initialized = true;
        }
      });
    }
  }

  public getSvgHtml(feature: DaisiFeature): string {
    return this.iconSvgByIdentifier.get(this.getIconIdentifier(feature))?.normal ?? '';
  }

  private startAddingIcons(map: MaplibreMap) {
    effect(
      () => {
        this.imDataService.allFeatures().forEach((feature) => {
          const neededIcon = this.getIconIdentifier(feature);
          if (!this.neededIcons.has(neededIcon)) {
            this.neededIcons.add(neededIcon);
          }
        });
        if (this.iconSvgByIdentifier.size != this.neededIcons.size) {
          this.neededIcons.forEach((identifier) => {
            if (!this.iconSvgByIdentifier.has(identifier)) {
              this.createSvgs(identifier, map);
            }
          });
          this.updateMaps();
        }
      },
      { injector: this.injector },
    );
  }

  private updateMaps() {
    this.addedIconsByMap.forEach((alreadyAdded, map) => {
      this.neededIcons.forEach((identifier) => {
        if (!alreadyAdded.has(identifier)) {
          this.addToMap(map, identifier);
        }
      });
    });
  }

  private getIconIdentifier(feature: DaisiFeature): string {
    const incidentStatus = feature.properties?.['incidentStatus'];
    const levelOfPersistencePercentage = feature.properties?.['daisiLevelOfPersistence'];
    const baseImageId =
      IncidentIconService.DATEX_TYPE_TO_MAP_ICON_MAP[
        feature.properties?.['type'] as keyof typeof IncidentIconService.DATEX_TYPE_TO_MAP_ICON_MAP
      ] || 'ndw-im-other-red-25';
    return `incident_${incidentStatus}_${baseImageId}_${levelOfPersistencePercentage}`;
  }

  private createSvgs(identifier: string, map: MaplibreMap) {
    const settings = identifier.split('_');
    const baseImage = map.getImage(settings[2]);
    const levelOfPersistencePercentage = Number(settings[3]);
    let incidentStatus = settings[1] as IncidentStatus;
    const normalSvg = this.createIncidentCircleSVG(
      incidentStatus,
      levelOfPersistencePercentage,
      baseImage,
      IncidentIconService.NORMAL_SIZE,
    );
    const largeSvg = this.createIncidentCircleSVG(
      incidentStatus,
      levelOfPersistencePercentage,
      baseImage,
      IncidentIconService.LARGE_SIZE,
    );
    this.iconSvgByIdentifier.set(identifier, { normal: normalSvg, large: largeSvg });
  }

  private addToMap(map: MaplibreMap, identifier: string) {
    if (this.addedIconsByMap.get(map)?.has(identifier)) {
      return; // already added
    }
    this.addedIconsByMap.get(map)?.add(identifier);

    let svgs = this.iconSvgByIdentifier.get(identifier)!;
    this.addSvg(map, identifier, IncidentIconService.NORMAL_SIZE, svgs.normal);
    this.addSvg(map, identifier, IncidentIconService.LARGE_SIZE, svgs.large);
  }

  private addSvg(map: MaplibreMap, identifier: string, scale: number, svg: string) {
    const image = new Image();
    image.onload = () => map.addImage(identifier + '_' + scale, image);
    image.onerror = (error) => {
      console.error('Error while adding image ' + identifier + '_' + scale + ' to the map.', error);
    };
    image.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  private createIncidentCircleSVG(
    incidentStatus: IncidentStatus,
    levelOfPersistence: number,
    styleImage: StyleImage,
    scale: number,
  ): string {
    const percentage = Math.max(0, Math.min(100, levelOfPersistence));
    const imageData = this.mapLibreImageToPng(styleImage);

    const radius = 12 * scale;
    const size = 28 * scale;
    const center = size / 2;
    const circleWidth = 3 * scale;

    const coloredAngle = (percentage / 100) * 2 * Math.PI;

    let coloredPath = '';
    let grayPath = '';

    if (percentage <= 0) {
      // Only gray full circle
      grayPath = `M ${center} ${center - radius} A ${radius} ${radius} 0 1 1 ${center} ${center + radius} A ${radius} ${radius} 0 1 1 ${center} ${center - radius}`;
    } else if (percentage >= 100) {
      // Only colored full circle
      coloredPath = `M ${center} ${center - radius} A ${radius} ${radius} 0 1 1 ${center} ${center + radius} A ${radius} ${radius} 0 1 1 ${center} ${center - radius}`;
    } else {
      const coloredX = center + radius * Math.sin(coloredAngle);
      const coloredY = center - radius * Math.cos(coloredAngle);

      const largeArcFlagRed = coloredAngle > Math.PI ? 1 : 0;
      const largeArcFlagGray = coloredAngle > Math.PI ? 0 : 1;

      coloredPath = `M ${center} ${center - radius} A ${radius} ${radius} 0 ${largeArcFlagRed} 1 ${coloredX} ${coloredY}`;
      grayPath = `M ${coloredX} ${coloredY} A ${radius} ${radius} 0 ${largeArcFlagGray} 1 ${center} ${center - radius}`;
    }

    return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
                <image href="${imageData}" width="${size - 2}" height="${size - 2}" transform="translate(1,1)" />
                ${coloredPath ? `<path d="${coloredPath}" fill="none" stroke="${this.getColor(incidentStatus)}" stroke-width="${circleWidth}" />` : ''}
                ${grayPath ? `<path d="${grayPath}" fill="none" stroke="lightgray" stroke-width="${circleWidth}" />` : ''}
              </svg>`;
  }

  private getColor(status: IncidentStatus) {
    switch (status) {
      case IncidentStatus.REPORTED:
        return '#cc4100';
      case IncidentStatus.CONFIRMED:
        return '#0070d1';
      case IncidentStatus.RESOLVED:
        return '#22a62d';
      default:
        return 'gray';
    }
  }

  private mapLibreImageToPng(styleImage: StyleImage): string | null {
    const { width, height, data } = styleImage.data;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    const imageData = ctx.createImageData(width, height);
    imageData.data.set(data);
    ctx.putImageData(imageData, 0, 0);

    return canvas.toDataURL('image/png');
  }
}
